// src/components/CareJourney/CareRecommendationScreen.jsx

import { useNavigate } from "react-router-dom";
import {
  getLifeStages,
  getLifeStageDetail,
  getLifeStageMeta,
  formatStageLabel,
} from "../Reusablesteps/Resolvelifestage";
import StepperProgress from "../Reusablesteps/Stepperprogress";
import { BtnBack, BtnContinue } from "../Reusablecomponents/Button/Reusableutton";

// ── Design tokens ─────────────────────────────────────────────────────────────
const T = {
  purple900: "#1A0A3C",
  purple700: "var(--primary-color)",
  purple600: "#4C24A8",
  purpleBtn: "var(--primary-color)",
  purpleLight: "var(--ava-bg-primary-light)",
  purpleBorder: "var(--primary-color)",
  lavenderBg: "var(--ava-bg-primary-light)",
  heading: "#1A1033",
  sub: "#6B7280",
  muted: "#9CA3AF",
  border: "#E5E7EB",
  body: "#374151",
};

const JOURNEY_LABELS = {
  pcos: "PCOS / Irregular Period",
  pregnancy: "Pregnancy Start",
  fertility: "Fertility / Preconception",
  postpartum: "Postpartum",
  menopause: "Perimenopause / Menopause",
  general: "General Women's Health",
};

const SYMPTOM_ICONS = {
  "Irregular periods": "🗓️", "Acne": "🫧", "Fatigue": "😴",
  "Weight gain": "⚖️", "Pelvic pain": "💜", "Mood swings": "🧠",
  "Hot flushes": "🌡️", "Nausea": "🤢", "Bloating": "🫁",
  "Facial hair growth": "✨", "Hair thinning": "💆",
  "Breast tenderness": "🌸", "Headaches/migraines": "🤕",
  "None of the above": "✅",
};
const getSymptomIcon = (s) => SYMPTOM_ICONS[s] || "💊";

const NEXT_STEPS = [
  {
    icon: "📅",
    iconBg: "#FFE4E4",
    title: "Book Gynaecologist Consultation",
    desc: "Consult a women's health specialist to discuss your symptoms and next steps.",
  },
  {
    icon: "🛡️",
    iconBg: "var(--ava-bg-primary-light)",
    title: "Take PCOS Risk Check",
    desc: "Complete a quick assessment to understand your hormonal risk level.",
  },
  {
    icon: "📋",
    iconBg: "var(--ava-bg-primary-light)",
    title: "Take Full Assessment",
    desc: "Answer guided questions to help evaluate your symptoms and health patterns.",
  },
];

// ── SVG: Flower icon (pathway card) ──────────────────────────────────────────
function FlowerIcon() {
  return (
    <svg width="64" height="64" viewBox="0 0 64 64" fill="none" style={{ flexShrink: 0 }}>
      <circle cx="32" cy="32" r="32" fill="#FFF0F5" />
      {/* petals */}
      {[0, 60, 120, 180, 240, 300].map((deg, i) => (
        <ellipse
          key={deg}
          cx="32" cy="18"
          rx="6" ry="10"
          fill={i % 2 === 0 ? "#F9A8D4" : "#F472B6"}
          opacity={i % 2 === 0 ? 0.9 : 0.7}
          transform={`rotate(${deg} 32 32)`}
        />
      ))}
      <circle cx="32" cy="32" r="10" fill="#FDE68A" />
      <circle cx="32" cy="32" r="6" fill="#FCD34D" />
    </svg>
  );
}

// ── SVG: Female avatar (snapshot) ────────────────────────────────────────────
function AvatarSVG() {
  return (
    <svg width="52" height="52" viewBox="0 0 52 52" fill="none" style={{ flexShrink: 0 }}>
      <circle cx="26" cy="26" r="26" fill="#FFF0F5" />
      {/* hair back */}
      <ellipse cx="26" cy="22" rx="14" ry="15" fill="#92400E" />
      {/* face */}
      <ellipse cx="26" cy="25" rx="10" ry="11" fill="#FDDCB0" />
      {/* hair top */}
      <ellipse cx="26" cy="14" rx="11" ry="7" fill="#78350F" />
      {/* eyes */}
      <ellipse cx="22.5" cy="23" rx="1.3" ry="1.5" fill="#1C1917" />
      <ellipse cx="29.5" cy="23" rx="1.3" ry="1.5" fill="#1C1917" />
      {/* blush */}
      <ellipse cx="20" cy="27" rx="2.5" ry="1.4" fill="#FCA5A5" opacity="0.5" />
      <ellipse cx="32" cy="27" rx="2.5" ry="1.4" fill="#FCA5A5" opacity="0.5" />
      {/* smile */}
      <path d="M22.5 28.5 Q26 32 29.5 28.5" stroke="#C2856A" strokeWidth="1.2" fill="none" strokeLinecap="round" />
      {/* neck */}
      <rect x="23" y="34" width="6" height="5" rx="2" fill="#FDDCB0" />
      {/* top */}
      <ellipse cx="26" cy="47" rx="15" ry="10" fill="#C4B5FD" />
    </svg>
  );
}

// ── SVG: Snapshot row icons ───────────────────────────────────────────────────
function SnapIcon({ type }) {
  if (type === "calendar") return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <rect x="1.5" y="3" width="17" height="15.5" rx="2.5" stroke="var(--primary-color)" strokeWidth="1.4" fill="var(--ava-bg-primary-light)" />
      <rect x="5.5" y="1" width="2" height="4" rx="1" fill="var(--primary-color)" />
      <rect x="12.5" y="1" width="2" height="4" rx="1" fill="var(--primary-color)" />
      <line x1="1.5" y1="8" x2="18.5" y2="8" stroke="var(--primary-color)" strokeWidth="1.2" />
      <rect x="4.5" y="11" width="2.5" height="2.5" rx="0.5" fill="var(--primary-color)" />
      <rect x="8.8" y="11" width="2.5" height="2.5" rx="0.5" fill="var(--primary-color)" />
      <rect x="13" y="11" width="2.5" height="2.5" rx="0.5" fill="var(--primary-color)" />
    </svg>
  );
  if (type === "leaf") return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M10 18C10 18 2.5 14 2.5 7.5C2.5 3.5 6 1.5 10 1.5C14 1.5 17.5 3.5 17.5 7.5C17.5 14 10 18 10 18Z" fill="#D1FAE5" stroke="#059669" strokeWidth="1.4" />
      <path d="M10 18L10 9" stroke="#059669" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M10 13C10 13 6.5 10 5.5 7.5" stroke="#059669" strokeWidth="1" strokeLinecap="round" />
    </svg>
  );
  if (type === "cycle") return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <circle cx="10" cy="10" r="7.5" stroke="var(--primary-color)" strokeWidth="1.4" fill="var(--ava-bg-primary-light)" strokeDasharray="3.5 2" />
      <circle cx="10" cy="10" r="2.5" fill="var(--primary-color)" />
      <path d="M10 3.5L10 5.5" stroke="var(--primary-color)" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M14.5 5.5L13 7" stroke="var(--primary-color)" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
  if (type === "drop") return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M10 2C10 2 4 9 4 13C4 16.3 6.7 19 10 19C13.3 19 16 16.3 16 13C16 9 10 2 10 2Z" fill="#FEE2E2" stroke="#F87171" strokeWidth="1.4" />
      <path d="M7.5 14C7.5 15.5 8.6 16.8 10 17.2" stroke="#F87171" strokeWidth="1.1" strokeLinecap="round" />
    </svg>
  );
  return null;
}

// ── SVG: Check circle ─────────────────────────────────────────────────────────
function CheckCircle() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}>
      <circle cx="10" cy="10" r="9" stroke={T.purple700} strokeWidth="1.5" fill={T.purpleLight} />
      <path d="M6.5 10L8.8 12.3L13.5 7.5" stroke={T.purple700} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ── Snapshot row ──────────────────────────────────────────────────────────────
function SnapRow({ iconType, label, value }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: `1px solid ${T.border}` }}>
      <div style={{ width: 36, height: 36, borderRadius: 10, background: T.purpleLight, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <SnapIcon type={iconType} />
      </div>
      <div>
        <p style={{ fontSize: 11, color: T.muted, margin: 0, fontFamily: "inherit" }}>{label}</p>
        <p style={{ fontSize: 13, fontWeight: 700, color: T.heading, margin: 0, fontFamily: "inherit" }}>{value}</p>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function CareRecommendationScreen({ allData = {}, onBack,stepIndex ,totalSteps}) {
  const navigate = useNavigate();

 // AFTER:
const {
  selectedJourney,
  selectedSymptoms = [],
  hasUrgent,
  profile = {},
  lifeStageAnswers = {},
  duration,
} = allData;

  const journeyBase = JOURNEY_LABELS[selectedJourney] || "General Women's Health";
  const pathwayTitle = `${journeyBase} Pathway`;

  // const stages = lifeStageAnswers?.stages || getLifeStages(profile?.age, profile?.marital);
  const stages = (lifeStageAnswers?.stages?.length > 0)
  ? lifeStageAnswers.stages
  : getLifeStages(profile?.age, profile?.marital);
  const primaryStage = stages[0] || "youngAdult";
  const stageDetail = getLifeStageDetail(primaryStage) || {};
  const stageMeta = getLifeStageMeta(primaryStage);
  const stageName = stageDetail.name || formatStageLabel(primaryStage);
 const p = JSON.parse(localStorage.getItem("patientPersona") || "{}");
  const patientName = (() => {
    try {
      // const p = JSON.parse(localStorage.getItem("patientPersona") || "{}");
      return p.name || profile.name || "Patient";
    } catch { return profile.name || "Patient"; }
  })();

  const ageRange = p.age
    ? `${Math.max(18, Number(p.age) - 3)}–${Number(p.age) + 2}`
    : "—";
// lmp is stored under lifeStageAnswers.lmp by BasicLifeStageScreen
const lastPeriod = allData.lastPeriod || lifeStageAnswers?.lmp || "";

  const lastPeriodStr = lastPeriod
    ? new Date(lastPeriod).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
    : "—";

  return (
      <>

        {/* ── Header ── */}
        <div className="scj-header">
        <div>
            <h1 style={{ fontSize: 16, fontWeight: 800, color: T.heading, marginBottom: 8, letterSpacing: "-0.5px" }}>
            Your Care Recommendation
          </h1>
          <p style={{ fontSize: 13, color: T.sub, margin: "0 0 14px" }}>
            Based on your answers, here is your personalised next step.
          </p>
        </div>
          <StepperProgress current={stepIndex} total={totalSteps} />
          {/* Stage badge */}
          {/* <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#FFF0F5", borderRadius: 99, padding: "6px 16px", border: "1px solid #FBCFE8" }}>
            <span style={{ fontSize: 14 }}>{stageMeta?.emoji || "🌸"}</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: "#BE185D" }}>{stageName}</span>
          </div> */}
        </div>

        {/* ── Two-column layout ── */}
        <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>

          {/* ══ LEFT COLUMN ══ */}
          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 14 }}>

            {/* ── Pathway card ── */}
            <div style={{
              border: `1.5px solid ${T.purpleBorder}`,
              borderRadius: 18,
              background: "#FDFCFF",
              display: "flex",
              overflow: "hidden",
              flexWrap:"wrap"
            }}>
              {/* Left band */}
              <div style={{ flex: 1, padding: "20px 22px", display: "flex", flexDirection: "column", gap: 12 }}>
                {/* Icon + title row */}
                <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
                  <FlowerIcon />
                  <div style={{ flex: 1, paddingTop: 4 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
                      <h2 style={{ fontSize: 14, fontWeight: 800, color: T.heading, margin: 0 }}>
                        {pathwayTitle}
                      </h2>
                      <span style={{
                        fontSize: 9, fontWeight: 700, padding: "3px 11px", borderRadius: 99,
                        background: hasUrgent ? "#FEE2E2" : "#FEF3C7",
                        color: hasUrgent ? "#991B1B" : "#92400E",
                      }}>
                        {hasUrgent ? "Urgent concern" : "Moderate concern"}
                      </span>
                    </div>
                    <p style={{ fontSize: 10, color: T.sub, margin: 0, lineHeight: 1.5 }}>
                      This pathway is most aligned with your symptoms and health profile.
                    </p>
                  </div>
                </div>

                {/* Why this pathway box */}
                <div style={{ background: T.lavenderBg, borderRadius: 10, padding: "12px 14px", display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <div style={{ width: 26, height: 26, borderRadius: 7, background: T.purple700, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, flexShrink: 0, color: "#fff", fontWeight: 700 }}>
                    i
                  </div>
                  <div>
                    <p style={{ fontSize: 12, fontWeight: 700, color: T.purple700, margin: "0 0 4px" }}>Why this pathway?</p>
                    <p style={{ fontSize: 11, color: T.body, margin: 0, lineHeight: 1.5 }}>
                      {selectedSymptoms.length > 0
                        ? `Your responses indicate ${selectedSymptoms.slice(0, 3).join(", ")} — common indicators for this care path.`
                        : "Based on your profile and life stage, this pathway best matches your health needs."}
                    </p>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div style={{ width: 1, background: T.border, flexShrink: 0 }} />

              {/* Right: What this means */}
              <div style={{ width: 230, flexShrink: 0, padding: "20px 20px" }}>
                <p style={{ fontSize: 13, fontWeight: 700, color: T.heading, margin: "0 0 14px" }}>What this means</p>
                {[
                  "Early evaluation can improve outcomes",
                  "Personalised care helps manage symptoms",
                  "Timely support for long-term well-being",
                ].map((item) => (
                  <div key={item} style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 12 }}>
                    <CheckCircle />
                    <span style={{ fontSize: 10, color: T.body, lineHeight: 1.5 }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Symptoms strip ── */}
            {/* {selectedSymptoms.length > 0 && (
              <div style={{ background: "#fff", borderRadius: 14, padding: "14px 16px", border: `1px solid ${T.border}` }}>
                <p style={{ fontSize: 12, fontWeight: 700, color: T.heading, margin: "0 0 10px" }}>🩺 Symptoms you reported</p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {selectedSymptoms.map((s) => (
                    <div key={s} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: T.purpleLight, borderRadius: 99, padding: "5px 12px", border: `1px solid ${T.purpleBorder}` }}>
                      <span style={{ fontSize: 14 }}>{getSymptomIcon(s)}</span>
                      <span style={{ fontSize: 11, fontWeight: 600, color: T.purple700 }}>{s}</span>
                    </div>
                  ))}
                </div>
              </div>
            )} */}

            {/* ── Next steps — 3-column grid ── */}
           {/* ── Next steps — 3-column grid ── */}
<div>
  <p
    style={{
      fontSize: 14,
      fontWeight: 700,
      color: T.heading,
      margin: "0 0 10px",
    }}
  >
    Your recommended next steps
  </p>

  <div
    style={{
      display: "grid",
      gridTemplateColumns: "1fr 1fr 1fr",
      gap: 12,
    }}
  >
    {NEXT_STEPS.map((step) => (
      <div
        key={step.title}
        style={{
          display: "flex",
          flexDirection: "column",
          background: "#fff",
          border: `1px solid ${T.border}`,
          borderRadius: 14,
          padding: "16px 15px",
        }}
      >
        {/* ICON + TITLE ROW */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
            marginBottom: 12,
          }}
        >
          {/* Icon */}
          <div
            style={{
              width: 46,
              height: 46,
              borderRadius: 12,
              background: step.iconBg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 22,
              flexShrink: 0,
            }}
          >
            {step.icon}
          </div>

          {/* Title */}
          <p
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: T.heading,
              margin: 0,
              lineHeight: 1.4,
              flex: 1,
            }}
          >
            {step.title}
          </p>
        </div>

        {/* Desc */}
        <p
          style={{
            fontSize: 11,
            color: T.sub,
            margin: "0 0 10px",
            lineHeight: 1.55,
            flex: 1,
          }}
        >
          {step.desc}
        </p>

        {/* Link */}
        {/* <button
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: T.purple700,
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: 0,
            textAlign: "left",
          }}
        >
          Explore →
        </button> */}
      </div>
    ))}
  </div>
</div>

            {/* ── Disclaimer ── */}
            {/* <div style={{ background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 12, padding: "12px 16px", display: "flex", gap: 10, alignItems: "flex-start" }}>
              <span style={{ fontSize: 20, flexShrink: 0 }}>🔒</span>
              <div>
                <p style={{ fontSize: 12, fontWeight: 700, color: "#92400E", margin: "0 0 3px" }}>
                  This guidance supports, not replaces, clinical evaluation.
                </p>
                <p style={{ fontSize: 11, color: "#78350F", margin: 0, lineHeight: 1.5 }}>
                  Your care team will review your information to provide personalised recommendations.
                </p>
              </div>
            </div> */}
          </div>

          {/* ══ RIGHT SIDEBAR ══ */}
          <div style={{ width: 244, flexShrink: 0 }}>
            <div style={{ background: "#fff", borderRadius: 18, padding: "20px 18px", border: `1px solid ${T.border}`, boxShadow: "0 2px 16px rgba(61,26,138,0.06)" }}>
              <p style={{ fontSize: 15, fontWeight: 700, color: T.heading, margin: "0 0 14px" }}>Your Snapshot</p>

              {/* Avatar row */}
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                <AvatarSVG />
                <div>
                  <p style={{ fontSize: 14, fontWeight: 700, color: T.heading, margin: 0 }}>{patientName}</p>
                  {/* <p style={{ fontSize: 12, color: T.muted, margin: 0 }}>Age {profile.age || "—"}</p> */}
                </div>
              </div>

              {/* Rows */}
              <SnapRow iconType="calendar" label="Age group" value={p.ageGroup || "—"} />
              <SnapRow iconType="leaf" label="Life stage" value={stageName} />
              <SnapRow iconType="cycle" label="Cycle" value={duration || "—"} />
              <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0" }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: T.purpleLight, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <SnapIcon type="drop" />
                </div>
                <div>
                  <p style={{ fontSize: 11, color: T.muted, margin: 0 }}>Last period</p>
                  <p style={{ fontSize: 13, fontWeight: 700, color: T.heading, margin: 0 }}>{lastPeriodStr}</p>
                </div>
              </div>

              {/* Info note */}
              {/* <div style={{ marginTop: 10, background: T.lavenderBg, borderRadius: 10, padding: "10px 12px", display: "flex", gap: 8, alignItems: "flex-start" }}>
                <span style={{ fontSize: 14, flexShrink: 0 }}>ℹ️</span>
                <p style={{ fontSize: 10, color: T.sub, margin: 0, lineHeight: 1.55 }}>
                  You can update your profile or journey at any time from settings.
                </p>
              </div> */}
            </div>
          </div>
        </div>

        {/* ── Footer ── */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 0 0", marginTop: 14, borderTop: `1px solid ${T.border}` }}>
          {/* <button onClick={onBack} style={{ display: "flex", alignItems: "center", gap: 6, padding: "10px 20px", borderRadius: 10, fontSize: 13, fontWeight: 600, border: `1.5px solid ${T.border}`, background: "#fff", color: T.body, cursor: "pointer" }}>
            ← Back
          </button> */}
           <BtnBack variant="outline" onClick={onBack}/>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 14 }}>🔒</span>
            <span style={{ fontSize: 11, color: T.muted }}>Your data is secure and encrypted</span>
          </div>
                    <BtnContinue variant="primary" onClick={() => navigate("/book-appointment")} 
                    label="Continue to Booking" />

          {/* <button onClick={() => navigate("/book-appointment")} style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 24px", borderRadius: 12, fontSize: 13, fontWeight: 700, color: "#fff", border: "none", background: T.purpleBtn, cursor: "pointer", boxShadow: "0 4px 14px rgba(61,26,138,0.25)" }}>
            Continue to Booking →
          </button> */}
        </div>
      </>
  );
}